import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, MessageSquare, Copy, History, Pencil, Trash2, Plus, X, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { MESES_CORTOS, CAMPOS_MES, dinero, textoCelda } from './formato';

/**
 * Grilla tipo hoja de cálculo: filas con etiqueta + 12 meses (o 13 períodos) + total, celdas con número o
 * fórmula, nota por celda (punto ámbar + globo al pasar el mouse), menú por clic derecho,
 * edición en línea con doble clic / Enter, navegación con flechas, inspector a la derecha.
 *
 * props:
 *  filas: [{ id, nombre, grupo?, departamento?, centroCosto?, activo, nota, celdas:{m1..} }]
 *  columnasExtra: [{ campo, label, ancho? }]  (antes de los meses; ej. proyectos: monto, utilidad)
 *  agruparPor: 'grupo' | null
 *  totales: { meses:[N], total }   (fila de totales; se recibe del backend)
 *  camposMes, rotulos: las columnas del presupuesto (m1..mN y Ene… o P01…; S2-03)
 *  etiquetaFila(fila) → nodo JSX de la primera columna
 *  onCelda(filaId, campo, { formula|valor|nota }) → Promise
 *  onCopiar(filaId, campo, hastaMes) → Promise
 *  onHistorial(filaId, campo) → Promise<[]>
 *  onEditarFila(fila) · onEliminarFila(fila) · onAgregar() · puedeEditar
 *  ayudaFormula: string
 *  moneda: 'USD' | 'CRC'  (formato de los montos)
 *  onAjustar(filaId, pct) → Promise   ajusta toda la línea en un %
 *  camposCalculo: [campo, qué es, ejemplo][]   lo que se puede usar en un cálculo
 *  puestos: string[]   nombres de los puestos, para sugerirlos en PUESTO("
 *  lineasOtras: string[]   líneas de la otra sección con su prefijo («Ingresos:…»), para sugerirlas
 */
export default function GrillaMeses({
  filas, columnasExtra = [], columnasInfo = [], agruparPor = null, ordenGrupos = [], totales, etiquetaFila, tituloEtiqueta = 'Rubro',
  camposMes = CAMPOS_MES, rotulos = MESES_CORTOS,
  onCelda, onCopiar, onHistorial, onEditarFila, onEliminarFila, onAgregar, puedeEditar = true, ayudaFormula, vacio = 'Sin filas todavía.', moneda = 'USD',
  onAjustar = null, camposCalculo = null, puestos = [], lineasOtras = [], periodosReal = 0
}) {
  const usd = (n, o = {}) => dinero(n, { ...o, moneda });
  const campos = useMemo(() => [...columnasExtra.map(c => c.campo), ...camposMes], [columnasExtra, camposMes]);
  const ultimo = camposMes.length;
  const [sel, setSel] = useState(null);             // { filaId, campo }
  const [editando, setEditando] = useState(null);   // { filaId, campo, texto }
  const [menu, setMenu] = useState(null);           // { x, y, filaId, campo }
  const [colapsados, setColapsados] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState(null);
  const [notaEdit, setNotaEdit] = useState('');
  const [textoInsp, setTextoInsp] = useState('');
  const [refCursor, setRefCursor] = useState(null);   // modo "apuntar" de Excel: { filaId, campo, start, end }
  const inputRef = useRef(null);
  const inspectorRef = useRef(null);
  const insertando = useRef(false);
  const tablaRef = useRef(null);

  const enfocarGrilla = () => setTimeout(() => { try { tablaRef.current?.focus({ preventScroll: true }); } catch { /* nada */ } }, 0);
  const filaSel = sel ? filas.find(f => f.id === sel.filaId) : null;
  const celdaSel = filaSel ? filaSel.celdas[sel.campo] : null;

  useEffect(() => { setNotaEdit(celdaSel?.nota || ''); setHistorial(null); }, [sel?.filaId, sel?.campo, celdaSel?.nota]);
  useEffect(() => { setTextoInsp(textoCelda(celdaSel)); }, [sel?.filaId, sel?.campo, celdaSel?.formula, celdaSel?.valor]);

  // ── referencias con clic (como Excel): si hay una fórmula abierta y se hace clic en otra celda, se inserta su referencia
  const NOMBRE_CAMPO = { monto: 'Monto', utilidad_proyectada: 'Utilidad', utilidad_real: 'Real' };
  const refDe = (f, campo, filaActualId) => {
    const nombre = NOMBRE_CAMPO[campo] || rotulos[parseInt(campo.slice(1), 10) - 1];
    return f.id === filaActualId ? nombre : `[${f.nombre}].${nombre}`;
  };
  const esFormulaAbierta = (t) => /^\s*[=+]/.test(t || '');
  // Modo "apuntar" (Excel): con una fórmula abierta y el cursor después de un operador, las flechas
  // recorren la grilla y van escribiendo la referencia de la celda señalada; otra tecla la fija.
  const manejarFlechaEnFormula = (e) => {
    const dirs = { ArrowDown: [1, 0], ArrowUp: [-1, 0], ArrowRight: [0, 1], ArrowLeft: [0, -1] };
    if (!dirs[e.key] || !editando) return false;
    const el = inputRef.current; const texto = editando.texto;
    let rc = refCursor;
    if (!rc) {
      const caret = el?.selectionStart ?? texto.length;
      if (!/[=+\-*/(,]\s*$/.test(texto.slice(0, caret))) return false;   // después de un número no se apunta
      rc = { filaId: editando.filaId, campo: editando.campo, start: caret, end: caret };
    }
    e.preventDefault();
    const lista = filasVisibles.length ? filasVisibles : filas;
    const fi = Math.max(0, lista.findIndex(f => f.id === rc.filaId)); const ci = campos.indexOf(rc.campo);
    const [df, dc] = dirs[e.key];
    const f = lista[Math.max(0, Math.min(lista.length - 1, fi + df))]; const campo = campos[Math.max(0, Math.min(campos.length - 1, ci + dc))];
    const propia = f.id === editando.filaId && campo === editando.campo;
    const ref = propia ? '' : refDe(f, campo, editando.filaId);
    const nuevo = texto.slice(0, rc.start) + ref + texto.slice(rc.end);
    const nrc = { filaId: f.id, campo, start: rc.start, end: rc.start + ref.length };
    setEditando({ ...editando, texto: nuevo }); setRefCursor(nrc);
    setTimeout(() => { try { el.setSelectionRange(nrc.end, nrc.end); } catch { /* nada */ } }, 0);
    return true;
  };
  const intentarInsertarRef = (e, f, campo) => {
    const el = document.activeElement;
    const enInline = editando && el === inputRef.current;
    const enInsp = !editando && sel && el === inspectorRef.current;
    if (!enInline && !enInsp) return false;
    const filaActualId = enInline ? editando.filaId : sel.filaId;
    const campoActual = enInline ? editando.campo : sel.campo;
    const texto = enInline ? editando.texto : textoInsp;
    if (!esFormulaAbierta(texto)) return false;
    if (f.id === filaActualId && campo === campoActual) return false;
    e.preventDefault();                                   // el input conserva el foco
    const ref = refDe(f, campo, filaActualId);
    const i = el.selectionStart ?? texto.length; const j = el.selectionEnd ?? i;
    const nuevo = texto.slice(0, i) + ref + texto.slice(j);
    if (enInline) { setEditando({ ...editando, texto: nuevo }); setRefCursor({ filaId: f.id, campo, start: i, end: i + ref.length }); } else setTextoInsp(nuevo);
    insertando.current = true;
    setTimeout(() => { try { el.setSelectionRange(i + ref.length, i + ref.length); } catch { /* nada */ } }, 0);
    return true;
  };
  // Al abrir el editor el cursor va al FINAL (como F2 en Excel): si se seleccionara todo, el clic en otra
  // celda reemplazaría el "=" en vez de agregarle la referencia.
  const editandoKey = editando ? `${editando.filaId}-${editando.campo}` : null;
  useEffect(() => { const el = inputRef.current; if (editando && el) { el.focus(); const n = el.value.length; try { el.setSelectionRange(n, n); } catch { /* nada */ } } }, [editandoKey]);
  useEffect(() => {
    const cerrar = () => setMenu(null);
    window.addEventListener('click', cerrar); window.addEventListener('scroll', cerrar, true);
    return () => { window.removeEventListener('click', cerrar); window.removeEventListener('scroll', cerrar, true); };
  }, []);

  // Agrupación visual
  const grupos = useMemo(() => {
    if (!agruparPor) return [{ nombre: null, filas }];
    const mapa = new Map();
    for (const f of filas) { const g = f[agruparPor] || 'Sin grupo'; if (!mapa.has(g)) mapa.set(g, []); mapa.get(g).push(f); }
    const nombres = [...mapa.keys()].sort((a, b) => { const ia = ordenGrupos.indexOf(a), ib = ordenGrupos.indexOf(b); return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b); });
    return nombres.map(n => ({ nombre: n, filas: mapa.get(n) }));
  }, [filas, agruparPor, ordenGrupos]);

  const filasVisibles = useMemo(() => grupos.flatMap(g => (g.nombre && colapsados[g.nombre]) ? [] : g.filas), [grupos, colapsados]);
  const sumaFila = (f) => camposMes.reduce((s, m) => s + (f.celdas[m]?.valor || 0), 0);
  const sumaGrupo = (g, campo) => g.filas.reduce((s, f) => s + (f.activo ? (f.celdas[campo]?.valor || 0) : 0), 0);

  // ── edición ──
  // Último estimado (P10): los períodos con real no se editan.
  const esReal = (campo) => periodosReal > 0 && /^m\d+$/.test(campo) && parseInt(campo.slice(1), 10) <= periodosReal;
  const empezarEdicion = (f, campo, textoInicial) => {
    if (!puedeEditar || esReal(campo)) return;
    setSel({ filaId: f.id, campo });
    setEditando({ filaId: f.id, campo, texto: textoInicial != null ? textoInicial : textoCelda(f.celdas[campo]), porTecla: textoInicial != null });
  };
  const confirmarEdicion = async (mover = null) => {
    if (!editando) return;
    const { filaId, campo, texto } = editando;
    const f = filas.find(x => x.id === filaId);
    const actual = textoCelda(f?.celdas[campo]);
    setEditando(null); setRefCursor(null);
    enfocarGrilla();
    if (texto.trim() === actual.trim()) { if (mover) moverSel(mover); return; }
    setGuardando(true);
    try {
      const t = texto.trim();
      if (t.startsWith('=') || (t.startsWith('+') && Number.isNaN(Number(t)))) await onCelda(filaId, campo, { formula: t.replace(/^=/, '') });
      else await onCelda(filaId, campo, { valor: t === '' ? 0 : Number(t.replace(',', '.')) });
      if (mover) moverSel(mover);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'No se pudo guardar');
      setEditando({ filaId, campo, texto, porTecla: false });
    } finally { setGuardando(false); }
  };
  const moverSel = (dir) => {
    if (!sel) return;
    const lista = filasVisibles.length ? filasVisibles : filas;
    const idxF = Math.max(0, lista.findIndex(f => f.id === sel.filaId)); const idxC = campos.indexOf(sel.campo);
    let nf = idxF, nc = idxC;
    if (dir === 'abajo') nf = Math.min(lista.length - 1, idxF + 1);
    if (dir === 'arriba') nf = Math.max(0, idxF - 1);
    if (dir === 'der') nc = Math.min(campos.length - 1, idxC + 1);
    if (dir === 'izq') nc = Math.max(0, idxC - 1);
    setSel({ filaId: lista[nf].id, campo: campos[nc] });
  };
  const onKeyTabla = (e) => {
    if (editando) return;
    if (!sel) return;
    const f = filas.find(x => x.id === sel.filaId);
    if (e.key === 'ArrowDown') { e.preventDefault(); moverSel('abajo'); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moverSel('arriba'); }
    else if (e.key === 'Tab') { e.preventDefault(); moverSel(e.shiftKey ? 'izq' : 'der'); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); moverSel('der'); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); moverSel('izq'); }
    else if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); empezarEdicion(f, sel.campo); }
    else if (e.key === 'Delete' || e.key === 'Backspace') { if (puedeEditar && !esReal(sel.campo)) { e.preventDefault(); onCelda(sel.filaId, sel.campo, { valor: 0 }).catch(() => {}); } }
    else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && /[0-9=+\-.]/.test(e.key)) { e.preventDefault(); empezarEdicion(f, sel.campo, e.key); }
  };
  const guardarNota = async () => {
    if (!sel) return;
    setGuardando(true);
    try { await onCelda(sel.filaId, sel.campo, { nota: notaEdit }); toast.success(notaEdit ? 'Nota guardada' : 'Nota quitada'); }
    catch (e) { toast.error(e.response?.data?.error || 'No se pudo guardar la nota'); }
    finally { setGuardando(false); }
  };
  const copiarHasta = async (hastaMes) => {
    if (!sel) return;
    setGuardando(true);
    try { await onCopiar(sel.filaId, sel.campo, hastaMes); toast.success('Copiado'); }
    catch (e) { toast.error(e.response?.data?.error || 'No se pudo copiar'); }
    finally { setGuardando(false); }
  };
  const verHistorial = async () => {
    if (!sel) return;
    try { setHistorial(await onHistorial(sel.filaId, sel.campo)); } catch { setHistorial([]); }
  };
  const abrirMenu = (e, f, campo) => {
    e.preventDefault(); e.stopPropagation();
    setSel({ filaId: f.id, campo });
    const r = tablaRef.current?.getBoundingClientRect();
    setMenu({ x: e.clientX - (r?.left || 0), y: e.clientY - (r?.top || 0), filaId: f.id, campo });
  };

  const esMes = (campo) => campo.startsWith('m');
  const nombreCampo = (campo) => esMes(campo) ? rotulos[parseInt(campo.slice(1), 10) - 1] : (columnasExtra.find(c => c.campo === campo)?.label || campo);

  const renderCelda = (f, campo) => {
    const c = f.celdas[campo] || { valor: 0 };
    const activa = sel?.filaId === f.id && sel?.campo === campo;
    const enEdicion = editando?.filaId === f.id && editando?.campo === campo;
    const apuntada = refCursor?.filaId === f.id && refCursor?.campo === campo && !enEdicion;
    const v = Number(c.valor) || 0;
    return (
      <td key={campo} className={clsx('p-0 border-b border-r border-gray-100 relative', !f.activo && 'opacity-50', esReal(campo) && 'bg-slate-50')} data-real={esReal(campo) ? '1' : undefined}
          onMouseDown={(e) => intentarInsertarRef(e, f, campo)}
          onClick={() => { if (insertando.current) { insertando.current = false; return; } setSel({ filaId: f.id, campo }); enfocarGrilla(); }}
          onDoubleClick={() => empezarEdicion(f, campo)}
          onContextMenu={(e) => abrirMenu(e, f, campo)}>
        {enEdicion ? (
          <input ref={inputRef} value={editando.texto}
            onChange={(e) => setEditando({ ...editando, texto: e.target.value })}
            onKeyDown={(e) => {
              if (esFormulaAbierta(editando.texto) && manejarFlechaEnFormula(e)) return;
              if (refCursor && !['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) setRefCursor(null);
              if (e.key === 'Enter') { e.preventDefault(); confirmarEdicion(e.shiftKey ? 'arriba' : 'abajo'); }
              else if (e.key === 'Tab') { e.preventDefault(); confirmarEdicion(e.shiftKey ? 'izq' : 'der'); }
              else if (e.key === 'Escape') { setEditando(null); setRefCursor(null); enfocarGrilla(); }
              else if (editando.porTecla && !esFormulaAbierta(editando.texto) && ['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(e.key)) {
                e.preventDefault(); confirmarEdicion({ ArrowDown: 'abajo', ArrowUp: 'arriba', ArrowRight: 'der', ArrowLeft: 'izq' }[e.key]);
              }
            }}
            onBlur={() => confirmarEdicion()}
            className="w-full h-8 px-2 text-right font-mono text-[12.5px] outline-none ring-2 ring-indigo-500 bg-white" />
        ) : (
          <div className={clsx('h-8 px-2 flex items-center justify-end font-mono text-[12.5px] tabular-nums cursor-cell select-none',
              activa && 'ring-2 ring-inset ring-indigo-500 bg-indigo-50/60',
              apuntada && 'outline outline-2 outline-dashed outline-indigo-500 -outline-offset-2 bg-indigo-50',
              v === 0 && 'text-gray-300', v < 0 && 'text-red-600', c.nota && 'nota-globo')}
              data-nota={c.nota || undefined}>
            {c.formula && <span className="absolute right-0.5 top-0.5 w-0 h-0 border-t-[6px] border-l-[6px] border-t-indigo-500 border-l-transparent" aria-hidden="true" />}
            {c.nota && <span className="absolute left-1 top-1.5 w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" />}
            {usd(v, { cero: '–' })}
          </div>
        )}
      </td>
    );
  };

  const anchoEtiqueta = 'min-w-[260px] w-[260px]';
  return (
    // El inspector aparece solo con una celda elegida: sin selección, la grilla usa todo el ancho.
    <div className={clsx('grid gap-3 items-start', sel && filaSel && 'xl:grid-cols-[minmax(0,1fr)_300px]')}>
      <div ref={tablaRef} className="relative bg-white border border-gray-200 rounded-xl overflow-hidden min-w-0 outline-none focus:ring-1 focus:ring-indigo-200" tabIndex={0} onKeyDown={onKeyTabla}>
        <div className="overflow-x-auto">
          <table className="border-separate border-spacing-0 w-full text-sm" style={{ minWidth: 260 + columnasInfo.length * 130 + (campos.length + 1) * 96 }}>
            <thead>
              <tr>
                <th className={clsx('sticky left-0 z-20 bg-gray-50 text-left text-[10.5px] font-semibold tracking-wider uppercase text-gray-500 px-3 py-2 border-b border-r border-gray-200', anchoEtiqueta)}>{tituloEtiqueta}</th>
                {columnasInfo.map(ci => <th key={ci.label} className="bg-gray-50 text-left text-[10.5px] font-semibold tracking-wider uppercase text-gray-500 px-2 py-2 border-b border-r border-gray-200 whitespace-nowrap">{ci.label}</th>)}
                {campos.map(c => <th key={c} className="bg-gray-50 text-right text-[10.5px] font-semibold tracking-wider uppercase text-gray-500 px-2 py-2 border-b border-r border-gray-200 whitespace-nowrap">{nombreCampo(c)}{esReal(c) && <span className="block text-[9px] font-medium normal-case tracking-normal text-slate-500" title="Real de la contabilidad: no se edita">real</span>}</th>)}
                <th className="bg-gray-50 text-right text-[10.5px] font-semibold tracking-wider uppercase text-gray-500 px-2 py-2 border-b border-gray-200">Total</th>
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 && (
                <tr><td colSpan={campos.length + 2} className="px-4 py-10 text-center text-gray-400 text-sm">{vacio}</td></tr>
              )}
              {grupos.map(g => renderGrupo(g))}
            </tbody>
            {totales && (
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td className={clsx('sticky left-0 z-10 bg-gray-50 px-3 py-2 border-t-2 border-r border-gray-300 text-sm', anchoEtiqueta)}>{camposMes.length === 13 ? 'Total del período' : 'Total del mes'}</td>
                  {columnasInfo.map(ci => <td key={ci.label} className="border-t-2 border-r border-gray-300" />)}
                  {columnasExtra.map(c => <td key={c.campo} className="px-2 py-2 border-t-2 border-r border-gray-300 text-right font-mono text-[12.5px]">{totales[c.campo] != null ? usd(totales[c.campo]) : ''}</td>)}
                  {totales.meses.map((v, i) => <td key={i} className={clsx('px-2 py-2 border-t-2 border-r border-gray-300 text-right font-mono text-[12.5px]', v < 0 && 'text-red-600')}>{usd(v)}</td>)}
                  <td className="px-2 py-2 border-t-2 border-gray-300 text-right font-mono text-[12.5px] text-indigo-700 bg-indigo-50">{usd(totales.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-4 px-3 py-2 border-t border-gray-100 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5"><i className="inline-block w-0 h-0 border-t-[7px] border-l-[7px] border-t-indigo-500 border-l-transparent" />celda con cálculo</span>
          <span className="inline-flex items-center gap-1.5"><i className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />celda con nota · pasá el mouse para leerla</span>
          <span>Doble clic o Enter para editar · con = o + abierta, flechas o clic señalan la celda a usar · clic derecho: nota, copiar, historial</span>
          {onAgregar && puedeEditar && (
            <button onClick={onAgregar} className="ml-auto inline-flex items-center gap-1 px-3 py-1 border-2 border-indigo-500 text-indigo-600 rounded-lg hover:bg-indigo-50 text-xs font-medium"><Plus size={14} /> Agregar fila</button>
          )}
        </div>

        {menu && (
          <div className="absolute z-30 bg-white border border-gray-200 rounded-lg shadow-xl py-1 text-sm min-w-[200px]" style={{ left: Math.min(menu.x, (tablaRef.current?.clientWidth || 600) - 220), top: menu.y }} onClick={(e) => e.stopPropagation()}>
            {puedeEditar && <button className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2" onClick={() => { const f = filas.find(x => x.id === menu.filaId); setMenu(null); empezarEdicion(f, menu.campo); }}><Pencil size={14} /> Editar monto o cálculo</button>}
            {puedeEditar && <button className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2" onClick={() => { setMenu(null); setTimeout(() => document.getElementById('nota-celda')?.focus(), 50); }}><MessageSquare size={14} className="text-amber-500" /> {celdaSel?.nota ? 'Editar nota' : 'Agregar nota'}</button>}
            {puedeEditar && esMes(menu.campo) && parseInt(menu.campo.slice(1), 10) < ultimo && <button className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2" onClick={() => { setMenu(null); copiarHasta(ultimo); }}><Copy size={14} /> Copiar hasta {rotulos[ultimo - 1]}</button>}
            <button className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2" onClick={() => { setMenu(null); verHistorial(); }}><History size={14} /> Historial de la celda</button>
            {onEditarFila && <button className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100 mt-1 pt-2" onClick={() => { const f = filas.find(x => x.id === menu.filaId); setMenu(null); onEditarFila(f); }}><Pencil size={14} /> Editar fila</button>}
            {onEliminarFila && puedeEditar && <button className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600 flex items-center gap-2" onClick={() => { const f = filas.find(x => x.id === menu.filaId); setMenu(null); onEliminarFila(f); }}><Trash2 size={14} /> Eliminar fila</button>}
          </div>
        )}
      </div>

      {/* Inspector de la celda seleccionada */}
      {sel && filaSel && (
      <aside className="bg-white border border-gray-200 rounded-xl p-4 space-y-3 xl:sticky xl:top-20">
          <>
            <div className="relative pr-6">
              <button onClick={() => { setSel(null); setHistorial(null); }} title="Cerrar" className="absolute right-0 top-0 text-gray-400 hover:text-gray-600"><X size={16} /></button>
              <div className="font-semibold text-gray-900 truncate" title={filaSel.nombre}>{filaSel.nombre} · {nombreCampo(sel.campo)}</div>
              <div className="text-[11px] font-mono tracking-wider text-gray-400 uppercase">{[filaSel.departamento, filaSel.centroCosto].filter(Boolean).join(' · ') || filaSel.grupo || ''}</div>
            </div>
            <PanelLinea fila={filaSel} total={sumaFila(filaSel)} usd={usd} puedeEditar={puedeEditar} onEditarFila={onEditarFila} onAjustar={onAjustar} />
            <div>
              <label className="block text-[10.5px] font-semibold tracking-wider uppercase text-gray-500 mb-1">Monto o cálculo</label>
              {esReal(sel.campo) && <p className="text-[11px] text-slate-600 mb-1" data-prueba="celda-real">Real de la contabilidad: en el último estimado no se edita.</p>}
              <EditorFormula celda={celdaSel} texto={textoInsp} setTexto={setTextoInsp} inputRef={inspectorRef} puedeEditar={puedeEditar && !esReal(sel.campo)} guardando={guardando}
                lineas={[...filas.map(x => x.nombre), ...lineasOtras]} puestos={puestos} mes={nombreCampo(sel.campo)}
                onGuardar={async (texto) => { const t = texto.trim(); try { if (t.startsWith('=') || (t.startsWith('+') && Number.isNaN(Number(t)))) await onCelda(sel.filaId, sel.campo, { formula: t.replace(/^=/, '') }); else await onCelda(sel.filaId, sel.campo, { valor: t === '' ? 0 : Number(t.replace(',', '.')) }); toast.success('Guardado'); } catch (e) { toast.error(e.response?.data?.error || 'Cálculo inválido'); throw e; } }} />
              {esFormulaAbierta(textoInsp) && textoInsp.trim() !== textoCelda(celdaSel).trim() && <div className="text-[11px] text-indigo-600 mt-1">Hacé clic en otra celda para insertarla en el cálculo.</div>}
              <div className="flex justify-between items-baseline text-xs text-gray-500 mt-1"><span>Resultado</span><b className="font-mono text-base text-gray-900">{usd(celdaSel?.valor)}</b></div>
            </div>
            {camposCalculo ? (
              <details className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2" data-prueba="ayuda-calculo">
                <summary className="cursor-pointer font-medium text-gray-700">Qué puede usar en un cálculo</summary>
                <p className="mt-2 text-gray-500 leading-relaxed">Un cálculo empieza con =. Con el cálculo abierto, hacé clic en otra celda para usarla.</p>
                <dl className="mt-2 space-y-1.5">
                  {camposCalculo.map(([campo, que, ejemplo]) => (
                    <div key={campo}><dt className="font-mono text-indigo-700">{campo}</dt><dd className="text-gray-500">{que} · <span className="font-mono text-gray-700">{ejemplo}</span></dd></div>
                  ))}
                </dl>
              </details>
            ) : ayudaFormula && <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2 leading-relaxed">{ayudaFormula}</div>}
            <div>
              <label htmlFor="nota-celda" className="block text-[10.5px] font-semibold tracking-wider uppercase text-gray-500 mb-1">Nota de esta celda</label>
              <textarea id="nota-celda" value={notaEdit} onChange={(e) => setNotaEdit(e.target.value)} disabled={!puedeEditar} rows={3}
                placeholder="Por qué este monto, de dónde salió, quién lo pidió…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-amber-50/60 focus:ring-2 focus:ring-amber-400 outline-none disabled:bg-gray-50" />
              {puedeEditar && (notaEdit || '') !== (celdaSel?.nota || '') && (
                <div className="flex gap-2 mt-1">
                  <button onClick={guardarNota} disabled={guardando} className="px-3 py-1 border-2 border-indigo-500 text-indigo-600 rounded-lg hover:bg-indigo-50 text-xs font-medium">Guardar nota</button>
                  <button onClick={() => setNotaEdit(celdaSel?.nota || '')} className="px-3 py-1 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-xs">Cancelar</button>
                </div>
              )}
            </div>
            {celdaSel?.actualizadoPor && <div className="text-xs text-gray-500">Último cambio: <b>{celdaSel.actualizadoPor}</b>{celdaSel.actualizadoEn ? ` · ${new Date(celdaSel.actualizadoEn).toLocaleDateString('es-CR')}` : ''}</div>}
            <div className="flex flex-wrap gap-2 pt-1">
              {puedeEditar && esMes(sel.campo) && parseInt(sel.campo.slice(1), 10) < ultimo && <button onClick={() => copiarHasta(ultimo)} disabled={guardando} className="px-3 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs inline-flex items-center gap-1"><Copy size={12} /> Copiar hasta {rotulos[ultimo - 1]}</button>}
              <button onClick={verHistorial} className="px-3 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs inline-flex items-center gap-1"><History size={12} /> Historial</button>
              {guardando && <Loader2 size={14} className="animate-spin text-indigo-500 self-center" />}
            </div>
            {historial && (
              <div className="border-t border-gray-100 pt-2 space-y-1 max-h-48 overflow-y-auto">
                <div className="flex justify-between items-center"><span className="text-[10.5px] font-semibold tracking-wider uppercase text-gray-500">Historial</span><button onClick={() => setHistorial(null)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button></div>
                {historial.length === 0 && <div className="text-xs text-gray-400">Sin cambios registrados.</div>}
                {historial.map(h => (
                  <div key={h.id} className="text-xs text-gray-600">
                    <span className="font-mono text-gray-400">{new Date(h.created_at).toLocaleDateString('es-CR')}</span> {h.usuario_nombre || 'Sistema'}: {h.formula_antes ? '=' + h.formula_antes : usd(h.valor_antes)} → <b>{h.formula_despues ? '=' + h.formula_despues : usd(h.valor_despues)}</b>
                    {(h.nota_antes || '') !== (h.nota_despues || '') && <span className="text-amber-600"> · nota</span>}
                  </div>
                ))}
              </div>
            )}
          </>
      </aside>
      )}
    </div>
  );

  function renderGrupo(g) {
    const cerrado = !!colapsados[g.nombre];
    return (
      <Fragment key={g.nombre || '_'}>
        {g.nombre && (
          <tr className="bg-gray-50/80">
            <td className={clsx('sticky left-0 z-10 bg-gray-50 px-3 py-1.5 border-b border-r border-gray-200', anchoEtiqueta)}>
              <button onClick={() => setColapsados({ ...colapsados, [g.nombre]: !cerrado })} className="flex items-center gap-2 font-semibold text-xs text-gray-700 uppercase tracking-wide">
                {cerrado ? <ChevronRight size={14} /> : <ChevronDown size={14} />}{g.nombre}
                <span className="font-normal normal-case tracking-normal text-gray-400">{g.filas.length} · {usd(g.filas.reduce((s, f) => s + (f.activo ? sumaFila(f) : 0), 0), { decimales: 0 })}</span>
              </button>
            </td>
            {columnasInfo.map(ci => <td key={ci.label} className="border-b border-r border-gray-200" />)}
            {campos.map(c => <td key={c} className="px-2 py-1.5 border-b border-r border-gray-200 text-right font-mono text-[11.5px] text-gray-500">{cerrado ? usd(sumaGrupo(g, c), { cero: '–', decimales: 0 }) : ''}</td>)}
            <td className="px-2 py-1.5 border-b border-gray-200 text-right font-mono text-[11.5px] text-gray-500">{cerrado ? usd(g.filas.reduce((s, f) => s + (f.activo ? sumaFila(f) : 0), 0), { decimales: 0 }) : ''}</td>
          </tr>
        )}
        {!cerrado && g.filas.map(f => (
          <tr key={f.id} className={clsx('group', sel?.filaId === f.id && 'bg-indigo-50/30')}>
            <td className={clsx('sticky left-0 z-10 bg-white group-hover:bg-indigo-50/40 px-3 py-0 border-b border-r border-gray-200 cursor-pointer', anchoEtiqueta, !f.activo && 'opacity-50')}
                onClick={() => onEditarFila && onEditarFila(f)}>
              {etiquetaFila ? etiquetaFila(f) : <span className="font-medium text-gray-900">{f.nombre}</span>}
            </td>
            {columnasInfo.map(ci => <td key={ci.label} className={clsx('px-2 border-b border-r border-gray-100 text-[12.5px] text-gray-600 whitespace-nowrap', !f.activo && 'opacity-50')}>{ci.render(f)}</td>)}
            {campos.map(c => renderCelda(f, c))}
            <td className={clsx('px-2 border-b border-gray-100 text-right font-mono text-[12.5px] bg-indigo-50/40 text-indigo-700 font-semibold', !f.activo && 'opacity-50')}>{usd(sumaFila(f))}</td>
          </tr>
        ))}
      </Fragment>
    );
  }
}

/** Lo que se sugiere mientras se escribe un cálculo: líneas después de «[» y puestos después de «PUESTO("». */
function sugerencias(texto, cursor, lineas, puestos) {
  const antes = texto.slice(0, cursor);
  if (!antes.trim().startsWith('=')) return null;
  let m = antes.match(/\[([^\][]*)$/);
  if (m) {
    const t = m[1].toLowerCase();
    return { tipo: 'linea', desde: cursor - m[1].length - 1, items: [...new Set(lineas)].filter(n => n.toLowerCase().includes(t)).slice(0, 8) };
  }
  m = antes.match(/PUESTO\(\s*"([^"]*)$/i);
  if (m) {
    const t = m[1].toLowerCase();
    return { tipo: 'puesto', desde: cursor - m[0].length, items: puestos.filter(n => n.toLowerCase().includes(t)).slice(0, 8) };
  }
  return null;
}

function EditorFormula({ celda, texto, setTexto, inputRef, onGuardar, puedeEditar, guardando, lineas = [], puestos = [], mes = 'Ene' }) {
  const cambiado = texto.trim() !== textoCelda(celda).trim();
  const [cursor, setCursor] = useState(null);
  const [activa, setActiva] = useState(0);
  const sug = cursor == null ? null : sugerencias(texto, cursor, lineas, puestos);
  const hay = sug && sug.items.length > 0;
  const elegir = (nombre) => {
    const pieza = sug.tipo === 'linea' ? `[${nombre}].${mes}` : `PUESTO("${nombre}")`;
    const nuevo = texto.slice(0, sug.desde) + pieza + texto.slice(cursor);
    const fin = sug.desde + pieza.length;
    setTexto(nuevo); setCursor(null); setActiva(0);
    setTimeout(() => { const el = inputRef.current; if (el) { el.focus(); try { el.setSelectionRange(fin, fin); } catch { /* nada */ } } }, 0);
  };
  const leerCursor = (e) => { setCursor(e.target.selectionStart); setActiva(0); };
  return (
    <div className="relative">
      <div className="flex gap-1">
        <input ref={inputRef} value={texto} onChange={(e) => { setTexto(e.target.value); leerCursor(e); }} onKeyUp={(e) => { if (!['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) setCursor(e.target.selectionStart); }} onClick={leerCursor} onBlur={() => setTimeout(() => setCursor(null), 150)} disabled={!puedeEditar}
          onKeyDown={(e) => {
            if (hay && e.key === 'ArrowDown') { e.preventDefault(); setActiva((activa + 1) % sug.items.length); return; }
            if (hay && e.key === 'ArrowUp') { e.preventDefault(); setActiva((activa - 1 + sug.items.length) % sug.items.length); return; }
            if (hay && (e.key === 'Enter' || e.key === 'Tab')) { e.preventDefault(); elegir(sug.items[activa]); return; }
            if (hay && e.key === 'Escape') { setCursor(null); return; }
            if (e.key === 'Enter' && cambiado) onGuardar(texto).catch(() => {});
          }}
          placeholder="Monto o =cálculo"
          className={clsx('flex-1 min-w-0 border rounded-lg px-2 py-1.5 font-mono text-sm outline-none focus:ring-2', texto.trim().startsWith('=') ? 'border-indigo-400 focus:ring-indigo-200 text-indigo-800' : 'border-gray-300 focus:ring-indigo-200')} />
        {puedeEditar && cambiado && <button onClick={() => onGuardar(texto).catch(() => {})} disabled={guardando} className="px-2 py-1 border-2 border-indigo-500 text-indigo-600 rounded-lg hover:bg-indigo-50 text-xs font-medium">OK</button>}
      </div>
      {hay && (
        <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-56 overflow-y-auto text-sm" data-prueba="sugerencias" role="listbox">
          <li className="px-3 pb-1 text-[10.5px] font-semibold tracking-wider uppercase text-gray-400">{sug.tipo === 'linea' ? 'Líneas' : 'Puestos'}</li>
          {sug.items.map((n, i) => (
            <li key={n} role="option" aria-selected={i === activa}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); elegir(n); }} className={clsx('w-full text-left px-3 py-1.5 truncate', i === activa ? 'bg-indigo-50 text-indigo-800' : 'hover:bg-gray-50')}>{n}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// De dónde salió la línea, como lo muestran los ERP.
const FUENTES = {
  contabilidad: ['De la contabilidad', 'bg-emerald-50 text-emerald-700'],
  plan_personal: ['Del plan de personal', 'bg-indigo-50 text-indigo-700'],
  recurrentes: ['De facturas recurrentes', 'bg-teal-50 text-teal-700'],
  inversiones: ['Del plan de inversiones', 'bg-sky-50 text-sky-700'],
  depreciacion: ['Del sub-libro de activos', 'bg-sky-50 text-sky-700'],
  importada: ['Importada', 'bg-amber-50 text-amber-800'],
  digitada: ['Digitada', 'bg-gray-100 text-gray-700']
};

/** La línea entera: de dónde sale, su total del año, repartirla y ajustarla en un %. */
function PanelLinea({ fila, total, usd, puedeEditar, onEditarFila, onAjustar }) {
  const [pct, setPct] = useState('');
  const [aplicando, setAplicando] = useState(false);
  useEffect(() => { setPct(''); }, [fila.id]);
  const [rotulo, clase] = FUENTES[fila.fuente] || ['Sin dato de origen', 'bg-gray-100 text-gray-500'];
  const generada = ['plan_personal', 'recurrentes', 'inversiones', 'depreciacion'].includes(fila.generada);
  const ajustar = async () => {
    setAplicando(true);
    try { await onAjustar(fila.id, Number(String(pct).replace(',', '.'))); toast.success(`Línea ajustada ${Number(pct) > 0 ? '+' : ''}${pct} %`); setPct(''); } catch (e) { toast.error(e.response?.data?.error || 'No se pudo ajustar'); } finally { setAplicando(false); }
  };
  return (
    <div className="space-y-2 pb-3 border-b border-gray-100" data-prueba="panel-linea">
      <div className="flex items-center justify-between gap-2">
        <span className={clsx('text-[11px] font-semibold px-2 py-0.5 rounded', clase)} data-prueba="fuente">{rotulo}</span>
        <span className="text-xs text-gray-500">Año <b className="font-mono text-gray-900" data-prueba="total-linea">{usd(total)}</b></span>
      </div>
      {fila.fuenteDetalle && <p className="text-xs text-gray-500 leading-relaxed" data-prueba="fuente-detalle">{fila.fuenteDetalle}</p>}
      {puedeEditar && (generada ? (
        onEditarFila && <button onClick={() => onEditarFila(fila)} className="px-3 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs">{fila.generada === 'recurrentes' ? 'Cambiar en facturación recurrente' : ['inversiones', 'depreciacion'].includes(fila.generada) ? 'Cambiar en Inversiones' : 'Cambiar en el plan de personal'}</button>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {onEditarFila && <button onClick={() => onEditarFila(fila)} className="px-3 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs" data-prueba="repartir">Repartir el año…</button>}
          {onAjustar && (
            <div className="flex items-center gap-1">
              <label htmlFor="ajuste-linea" className="sr-only">Porcentaje de ajuste</label>
              <input id="ajuste-linea" type="number" step="0.1" value={pct} onChange={(e) => setPct(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && pct) ajustar(); }} placeholder="%" className="w-16 border border-gray-300 rounded-lg px-2 py-1 text-xs font-mono text-right" />
              <button onClick={ajustar} disabled={!pct || Number(pct) === 0 || aplicando} className="px-3 py-1 border-2 border-indigo-500 text-indigo-600 rounded-lg hover:bg-indigo-50 text-xs font-medium disabled:opacity-50" data-prueba="ajustar">Ajustar %</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
